import { CreateCylinder } from '@babylonjs/core/Meshes/Builders/cylinderBuilder';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import type { Scene } from '@babylonjs/core/scene';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { PALETTE } from '@observant/shared';

/**
 * The player's Qubit: a flat neon hexagon facing the camera that shimmers —
 * fading and ghosting in and out as if slipping between superposition states.
 */
export interface QubitMesh {
  root: Mesh;
  /** Set the shell tint 0..1 (cyan→magenta), from the wasm collapse() value. */
  setCollapse(v: number): void;
  /** Advance the shimmer / ghost. */
  tick(t: number): void;
}

function hexMaterial(scene: Scene, name: string, color: Color3): StandardMaterial {
  const m = new StandardMaterial(name, scene);
  m.emissiveColor = color.clone();
  m.diffuseColor = new Color3(0, 0, 0);
  m.specularColor = new Color3(0, 0, 0);
  m.disableLighting = true;
  m.alpha = 1;
  return m;
}

function hexMesh(scene: Scene, name: string, diameter: number): Mesh {
  // A 6-sided cylinder = hexagonal prism; rotate so a flat face points at the camera (−Z).
  const m = CreateCylinder(name, { diameter, height: 0.22, tessellation: 6 }, scene);
  m.rotation.x = Math.PI / 2;
  return m;
}

export function createQubitMesh(scene: Scene): QubitMesh {
  const cyan = Color3.FromHexString(PALETTE.neonCyan);
  const magenta = Color3.FromHexString(PALETTE.neonMagenta);

  const mat = hexMaterial(scene, 'qubit-mat', cyan);
  const root = hexMesh(scene, 'qubit', 1.15);
  root.material = mat;

  // A ghost hexagon — the "other" superposition state, fading in and out.
  const ghostMat = hexMaterial(scene, 'qubit-ghost-mat', magenta);
  const ghost = hexMesh(scene, 'qubit-ghost', 1.15);
  ghost.material = ghostMat;
  ghost.parent = root;
  ghost.rotation.x = 0; // parent already rotates; keep ghost coplanar
  ghost.position.set(0, 0, 0);

  let tint = 0;
  const base = () => Color3.Lerp(cyan, magenta, tint);

  return {
    root,
    setCollapse(v) {
      tint = Math.max(0, Math.min(1, v));
    },
    tick(t) {
      // Irregular shimmer from layered sines → "in and out of superposition".
      const shimmer = 0.5 + 0.5 * Math.sin(t * 3.1) * Math.sin(t * 1.3 + 0.7);
      mat.alpha = 0.55 + 0.45 * shimmer;
      mat.emissiveColor = base().scale(0.7 + 0.6 * shimmer);
      root.rotation.y = Math.sin(t * 0.8) * 0.25;
      const s = 1 + 0.06 * Math.sin(t * 5);
      root.scaling.set(s, s, 1);

      // Ghost pulses opposite the main body and drifts, like a second path.
      const gp = 1 - shimmer;
      ghostMat.alpha = 0.45 * gp;
      ghost.position.set(0.28 * Math.sin(t * 2.2), 0.28 * Math.cos(t * 1.7), 0);
      ghost.scaling.set(0.7, 0.7, 1);
    },
  };
}
